import type { ClueDirection, PuzzleType } from '@arrow/puzzle-schema';

/**
 * Inline SVG icons. All inherit `currentColor`. The arrow is the game's language (the brand
 * mark is the Dart, DartMark.tsx): a solid wedge
 * (the same shape the board prints), straight or bent. Line icons use a bold 2.4 stroke,
 * never a 1px SaaS hairline.
 */

/** A solid wedge arrow pointing right, in a 24×24 box. */
const WEDGE = 'M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z';
/** Bent wedges (24×24): in from the left then down, and in from the top then right. */
const BENT_RIGHT_DOWN = 'M2 3.6h13.6v8.2H21L12.8 21.6 4.6 11.8h5.4V9.2H2z';
const BENT_DOWN_RIGHT = 'M3.6 2v13.6h8.2V21l9.8-8.2-9.8-8.2v5.4H9.2V2z';

export type WedgeDirection = ClueDirection | 'diagonal' | 'left';

/** The brand arrow. `diagonal` is the ↘ mark; `left` is back (“‹ Today”); the rest match a clue's direction. */
export function Wedge({ direction = 'diagonal', size, className }: { direction?: WedgeDirection; size?: number; className?: string }) {
	let d = WEDGE;
	let rotate = 0;
	if (direction === 'diagonal') rotate = 45;
	else if (direction === 'left') rotate = 180;
	else if (direction === 'down') rotate = 90;
	else if (direction === 'right-down') d = BENT_RIGHT_DOWN;
	else if (direction === 'down-right') d = BENT_DOWN_RIGHT;
	return (
		<svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
			<path d={d} transform={rotate ? `rotate(${rotate} 12 12)` : undefined} />
		</svg>
	);
}

/** The ↘ mark (logo, loading, the board's corner). */
export function LogoMark({ size = 18 }: { size?: number }) {
	return <Wedge direction="diagonal" size={size} />;
}

/** A clue's direction, as the wedge the board prints. */
export function DirectionGlyph({ direction, size = 18 }: { direction: ClueDirection; size?: number }) {
	return <Wedge direction={direction} size={size} />;
}

/** The streak's flame: vermilion with an ink keyline, printed like the rest (not an emoji). */
export function Flame({ size = 16, className = 'flame' }: { size?: number; className?: string }) {
	return (
		<svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
			<path d="M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z" />
		</svg>
	);
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

export const Icon = {
	back: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.4} aria-hidden="true">
			<path d="M8.5 5H21v14H8.5L2.5 12z" />
			<path d="m11.5 9 6 6m0-6-6 6" />
		</svg>
	),
	/** Switch direction: a line turning the corner. */
	swap: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.4} aria-hidden="true">
			<path d="M5 3.5v8.5a3 3 0 0 0 3 3h11" />
			<path d="m15 11 4 4-4 4" />
			<path d="M9 3.5h6.5" />
		</svg>
	),
	prev: () => (
		<svg viewBox="0 0 24 24" width={20} height={20} {...stroke} strokeWidth={2.6} aria-hidden="true">
			<path d="M14.5 6 8.5 12l6 6" />
		</svg>
	),
	next: () => (
		<svg viewBox="0 0 24 24" width={20} height={20} {...stroke} strokeWidth={2.6} aria-hidden="true">
			<path d="M9.5 6l6 6-6 6" />
		</svg>
	),
	share: () => (
		<svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
			<path d={WEDGE} />
		</svg>
	),
	check: () => (
		<svg viewBox="0 0 16 16" width={14} height={14} {...stroke} strokeWidth={2.6} aria-hidden="true">
			<path d="M3.5 8.5l3 3 6-7" />
		</svg>
	),
	more: () => (
		<svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
			<circle cx="5" cy="12" r="2.1" />
			<circle cx="12" cy="12" r="2.1" />
			<circle cx="19" cy="12" r="2.1" />
		</svg>
	),
	close: () => (
		<svg viewBox="0 0 24 24" width={20} height={20} {...stroke} strokeWidth={2.4} aria-hidden="true">
			<path d="M6 6l12 12M18 6 6 18" />
		</svg>
	),
	/** ≡: the app's menu, on every screen. */
	menu: () => (
		<svg viewBox="0 0 24 24" width={22} height={22} {...stroke} strokeWidth={2.4} aria-hidden="true">
			<path d="M4 7h16M4 12h16M4 17h16" />
		</svg>
	),
	help: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden="true">
			<circle cx="12" cy="12" r="9" />
			<path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14" />
			<circle cx="12" cy="17.2" r=".6" fill="currentColor" />
		</svg>
	),
	plus: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.6} aria-hidden="true">
			<path d="M12 5v14M5 12h14" />
		</svg>
	),
	sliders: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden="true">
			<path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
			<circle cx="15" cy="7" r="2" />
			<circle cx="9" cy="17" r="2" />
		</svg>
	),
	info: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.2} aria-hidden="true">
			<circle cx="12" cy="12" r="9" />
			<path d="M12 11v6M12 7.5v.1" />
		</svg>
	),
	chevron: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.6} aria-hidden="true">
			<path d="M9 5l7 7-7 7" />
		</svg>
	),
	/** Check this answer: one tick. */
	checkOne: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2.4} aria-hidden="true">
			<path d="M5 12.5l4.5 4.5L19 7.5" />
		</svg>
	),
	/** Check the puzzle: a tick in a grid. */
	checkAll: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden="true">
			<rect x="3.5" y="3.5" width="17" height="17" rx="1.5" />
			<path d="M8 12.2l2.8 2.8L16.5 9" strokeWidth={2.4} />
		</svg>
	),
	/** Reveal this answer: an eye. */
	reveal: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden="true">
			<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	),
	/** Dev tools: a wrench. */
	tools: () => (
		<svg viewBox="0 0 24 24" {...stroke} strokeWidth={2} aria-hidden="true">
			<path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5a4 4 0 0 1-2-2z" />
		</svg>
	),
};

/** Crossed swords: a challenge (Challenge a friend, Take the challenge, Send the rematch). */
export function Swords({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			className={className}
			fill="none"
			stroke="currentColor"
			strokeWidth={2.4}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M4 20 20 4M20 4v7M20 4h-7M4 4l6 6M4 4v5M4 4h5" />
		</svg>
	);
}

/** The lime "yes" tick in an ink ring (the mock's `tick()`): a win, a perfect solve. */
export function LimeTick({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 22 22" aria-hidden="true">
			<circle cx="11" cy="11" r="9.5" fill="var(--lime)" stroke="var(--mark-edge)" strokeWidth="1.8" />
			<path d="m6.6 11.3 3 3 5.8-6.2" fill="none" stroke="var(--on-lime)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/**
 * The install flow's icons (docs/mockups/install.html): our own (add, check, link, copy,
 * down, close) and small stand-ins for the browsers' buttons the guides point at (Safari's
 * ⋯ and Share, Chrome's ⋮, the compass). Line icons at the mock's weights.
 */
const line = (w: number) => ({ ...stroke, strokeWidth: w });
export const InstallIcon = {
	add: () => (
		<svg viewBox="0 0 24 24" {...line(2)} aria-hidden="true">
			<rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
			<path d="M12 8v8M8 12h8" />
		</svg>
	),
	check: () => (
		<svg viewBox="0 0 24 24" {...line(2.8)} aria-hidden="true">
			<path d="M5 12.5l4.5 4.5L19 7.5" />
		</svg>
	),
	close: () => (
		<svg viewBox="0 0 24 24" {...line(2.8)} aria-hidden="true">
			<path d="M6 6l12 12M18 6L6 18" />
		</svg>
	),
	/** iOS's Share: a box with an arrow out of the top. */
	share: () => (
		<svg viewBox="0 0 24 24" {...line(2)} aria-hidden="true">
			<path d="M12 3v12" />
			<path d="M8 7l4-4 4 4" />
			<path d="M7 10H6v11h12V10h-1" />
		</svg>
	),
	dots: () => (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
			<circle cx="5" cy="12" r="2" />
			<circle cx="12" cy="12" r="2" />
			<circle cx="19" cy="12" r="2" />
		</svg>
	),
	kebab: () => (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
			<circle cx="12" cy="5" r="2" />
			<circle cx="12" cy="12" r="2" />
			<circle cx="12" cy="19" r="2" />
		</svg>
	),
	/** Samsung Internet's menu: three lines. */
	burger: () => (
		<svg viewBox="0 0 24 24" {...line(2.2)} aria-hidden="true">
			<path d="M4 7h16M4 12h16M4 17h16" />
		</svg>
	),
	compass: () => (
		<svg viewBox="0 0 24 24" {...line(1.9)} aria-hidden="true">
			<circle cx="12" cy="12" r="9" />
			<path d="M15.5 8.5l-2 5-5 2 2-5z" />
		</svg>
	),
	link: () => (
		<svg viewBox="0 0 24 24" {...line(2)} aria-hidden="true">
			<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
			<path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
		</svg>
	),
	copy: () => (
		<svg viewBox="0 0 24 24" {...line(1.8)} aria-hidden="true">
			<rect x="8" y="8" width="12" height="12" rx="2" />
			<path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
		</svg>
	),
	down: () => (
		<svg viewBox="0 0 24 24" {...line(2.4)} aria-hidden="true">
			<path d="M12 4v11M7 10.5l5 5 5-5" />
			<path d="M5 20h14" />
		</svg>
	),
	back: () => (
		<svg viewBox="0 0 24 24" {...line(2.4)} aria-hidden="true">
			<path d="M15 5l-7 7 7 7" />
		</svg>
	),
	fwd: () => (
		<svg viewBox="0 0 24 24" {...line(2.4)} aria-hidden="true">
			<path d="M9 5l7 7-7 7" />
		</svg>
	),
	book: () => (
		<svg viewBox="0 0 24 24" {...line(1.8)} aria-hidden="true">
			<path d="M4 5.5C6.5 4 9.5 4 12 5.5 14.5 4 17.5 4 20 5.5v13c-2.5-1.5-5.5-1.5-8 0-2.5-1.5-5.5-1.5-8 0z" />
			<path d="M12 5.5v13" />
		</svg>
	),
};

/** A small carved pumpkin for the Halloween day chip (and the Halloween collection). */
export function Pumpkin() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className="pumpkin">
			<ellipse cx="12" cy="14" rx="9" ry="7.5" fill="currentColor" />
			<path d="M12 6.5c0-2 1-3 3-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M8.5 12l2-2 1 2.5zM15.5 12l-2-2-1 2.5z" fill="var(--pumpkin-cut)" />
			<path d="M8 16c1.2 1.4 6.8 1.4 8 0-1 2.4-7 2.4-8 0z" fill="var(--pumpkin-cut)" />
		</svg>
	);
}

/** A small fir for the Christmas day chip. */
export function Tree() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className="pumpkin">
			<path d="M12 2.5 6 10h3l-4 5h4l-4 5h14l-4-5h4l-4-5h3z" fill="currentColor" />
			<rect x="10.5" y="20" width="3" height="2.5" fill="currentColor" />
		</svg>
	);
}

/** A special puzzle type's small mark: the pumpkin or the tree, in `currentColor`. */
export function SeasonIcon({ type }: { type: PuzzleType }) {
	return type === 'halloween' ? <Pumpkin /> : <Tree />;
}
